import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Helmet } from 'react-helmet-async'; // Added Helmet import

import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { useTranslation } from 'react-i18next';
// import neoAIGlass from '../assets/NeoAI Glass.png'; // Unused
import NetworkIconLoader from '../components/NetworkIconLoader';
import { Lock, X, TicketPercent, ChevronDown, ChevronUp, ArrowRight, Check, Minus } from 'lucide-react';
import Footer from '../components/Footer';
// Liquid Glass Assets

// import BrandMarquee from '../components/BrandMarquee'; // Removed external import

// DATA MAGIC AI TOOLS (Dipindahkan ke sini agar satu file)
const magicAiTools = [
  { file: 'Add_Furniture.webp', label: 'Add Furniture' },
  { file: 'Art_Karikatur.webp', label: 'Art Karikatur' },
  { file: 'Baby_Born.webp', label: 'Baby Born' },
  { file: 'Buat_Banner.webp', label: 'Buat Banner' },
  { file: 'Desain_Bangunan.webp', label: 'Desain Bangunan' },
  { file: 'Edit_Foto.webp', label: 'Edit Foto' },
  { file: 'Face-swap.webp', label: 'Face Swap' },
  { file: 'Foto_Artis.webp', label: 'Foto Artis' },
  { file: 'Foto_Fashion__Produk_.webp', label: 'Foto Fashion' },
  { file: 'Foto_Make_Up.webp', label: 'Foto Make Up' },
  { file: 'Foto_Produk.webp', label: 'Foto Produk' },
  { file: 'Foto_Umrah_and_Haji.webp', label: 'Foto Umrah & Haji' },
  { file: 'Gambar_Sketsa.webp', label: 'Gambar Sketsa' },
  { file: 'Ganti_Angle_Camera.webp', label: 'Ganti Angle' },
  { file: 'Kamar_Pas.webp', label: 'Kamar Pas' },
  { file: 'Kids_Foto.webp', label: 'Kids Foto' },
  { file: 'Maternity.webp', label: 'Maternity' },
  { file: 'Mock_up.webp', label: 'Mock Up' },
  { file: 'Model_Generator.webp', label: 'Model Generator' },
  { file: 'Perluas_Foto.webp', label: 'Perluas Foto' },
  { file: 'Photobox.webp', label: 'Photobox' },
  { file: 'Prewedding.webp', label: 'Prewedding' },
  { file: 'Retouch_Foto.webp', label: 'Retouch Foto' },
  { file: 'Studio_Foto.webp', label: 'Studio Foto' },
  { file: 'Wedding.webp', label: 'Wedding' },
  { file: 'auto rapi.webp', label: 'Auto Rapi' },
  { file: 'gabung_foto.webp', label: 'Gabung Foto' },
  { file: 'pas-foto-warna.webp', label: 'Pas Foto Warna' },
  { file: 'perbaiki-foto.webp', label: 'Perbaiki Foto' },
  { file: 'ubah-angle-kamera.webp', label: 'Ubah Angle Check' },
  { file: 'ubah-foto-ke-miniatur.webp', label: 'Foto Miniatur' },
];

// Split rows untuk Marquee
const row1Tools = magicAiTools.slice(0, 16);
const row2Tools = magicAiTools.slice(16);


gsap.registerPlugin(ScrollTrigger);

// ====================
// GLOBAL IMAGE CACHE
// ====================
// Supaya ketika hook dipakai ulang di halaman yang sama, kita nggak bikin Image baru
// terus-terusan. Browser juga akan cache dari network, tapi ini nge-cache di memori JS.
const imageCache = {};

// ====================
// HOOK: Efek mengetik
// ====================
const useTypingEffect = (text, speed = 130, pause = 1200) => {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    let index = 0;
    let deleting = false;
    let timeoutId;

    const tick = () => {
      if (!deleting) {
        if (index < text.length) {
          setDisplay(text.slice(0, index + 1));
          index += 1;
          timeoutId = setTimeout(tick, speed);
        } else {
          deleting = true;
          timeoutId = setTimeout(tick, pause);
        }
      } else {
        if (index > 0) {
          setDisplay(text.slice(0, index - 1));
          index -= 1;
          timeoutId = setTimeout(tick, speed / 1.5);
        } else {
          deleting = false;
          timeoutId = setTimeout(tick, 400);
        }
      }
    };

    tick();
    return () => clearTimeout(timeoutId);
  }, [text, speed, pause]);

  return display;
};

// =====================================================
// HOOK: Animasi sequence WebP di dalam <canvas>
// - preload + decode semua frame dulu (Promise.all)
// - gambar di canvas pakai requestAnimationFrame
// - fps stabil pakai perhitungan waktu (bukan sekadar delta increment)
// =====================================================
const useImageSequenceCanvas = (frameCount, basePath, fps = 30) => {
  const canvasRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const images = new Array(frameCount);
    let rafId;
    let isCancelled = false;

    // ukuran yang dipakai buat render (tanpa DPR)
    let renderWidth = 0;
    let renderHeight = 0;
    let dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      renderWidth = rect.width;
      renderHeight = rect.height;

      // supaya 1 unit koordinat = 1 CSS pixel
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const drawFrame = (idx) => {
      const img = images[idx];
      if (!img || !renderWidth || !renderHeight) return;

      ctx.clearRect(0, 0, renderWidth, renderHeight);

      // efek "object-contain"
      const scale = Math.min(
        renderWidth / img.width,
        renderHeight / img.height
      );

      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const dx = (renderWidth - drawWidth) / 2;
      const dy = (renderHeight - drawHeight) / 2;

      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
    };

    const frameDurationMs = 1000 / fps;
    let startTime = performance.now();
    let lastRenderedFrame = -1;

    const animate = (time) => {
      if (isCancelled) return;

      const elapsed = time - startTime;
      const frameFloat = elapsed / frameDurationMs;
      const frameIndex = Math.floor(frameFloat) % frameCount;

      // Hanya redraw kalau frame-nya berubah (mengurangi kerjaan)
      if (frameIndex !== lastRenderedFrame) {
        lastRenderedFrame = frameIndex;
        drawFrame(frameIndex);
      }

      rafId = requestAnimationFrame(animate);
    };

    const loadAllImages = async () => {
      const promises = [];

      for (let i = 0; i < frameCount; i += 1) {
        const padded = String(i + 1).padStart(3, '0');
        const src = `${basePath}${padded}.webp`;

        const p = new Promise((resolve) => {
          // Kalau sudah pernah di-cache di memori JS, langsung pakai
          if (imageCache[src]) {
            images[i] = imageCache[src];
            resolve();
            return;
          }

          const img = new Image();
          img.src = src;

          img.onload = async () => {
            try {
              // Pastikan decode selesai sebelum animasi jalan
              if (img.decode) {
                await img.decode();
              }
            } catch (e) {
              // Kalau decode error, tetap lanjut saja pakai img apa adanya
            }
            imageCache[src] = img;
            images[i] = img;
            resolve();
          };

          img.onerror = (err) => {
            console.warn('Failed to load frame:', src, err);
            images[i] = null;
            resolve();
          };
        });

        promises.push(p);
      }

      await Promise.all(promises);
      if (isCancelled) return;

      // Cari frame pertama yang valid untuk ditampilkan
      const firstValidIndex = images.findIndex((img) => img);
      if (firstValidIndex === -1) {
        console.warn('Tidak ada frame yang berhasil di-load.');
        return;
      }

      // Tampilkan dulu frame pertama biar nggak blank
      drawFrame(firstValidIndex);

      // Mulai animasi setelah SEMUA (atau sebisa mungkin) frame ke-load & decode,
      // jadi saat loop ke-2, ke-3, dst harusnya lebih halus.
      startTime = performance.now();
      rafId = requestAnimationFrame(animate);
    };

    loadAllImages().then(() => setIsLoaded(true));

    return () => {
      isCancelled = true;
      window.removeEventListener('resize', resizeCanvas);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [frameCount, basePath, fps]);

  return { canvasRef, isLoaded };
};

// --- Ikon SVG Features ---
const features = [
  {
    id: 'socialMedia',
    icon: <img src="/assets/icon-tools/media-generator.webp" alt="Social Media Generator" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'tts',
    icon: <img src="/assets/icon-tools/text-to-speech.webp" alt="Text to Speech" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'imageToVideo',
    icon: <img src="/assets/icon-tools/video-generator.webp" alt="Image to Video" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'adsGenerator',
    icon: <img src="/assets/icon-tools/ads-generator.webp" alt="ADS Generator" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'imageEditing',
    icon: <img src="/assets/icon-tools/image-editor.webp" alt="Image Editing" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'briefToImages',
    icon: <img src="/assets/icon-tools/image-generator.webp" alt="Brief to Images" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'brandingGenerator',
    icon: <img src="/assets/icon-tools/branding-generator.webp" alt="Branding Generator" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'contentPlanner',
    icon: <img src="/assets/icon-tools/content-planner.webp" alt="Content Planner" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'magicAi',
    icon: <img src="/assets/icon-tools/magic-ai.webp" alt="Magic AI" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
  {
    id: 'ugcGenerator',
    icon: <img src="/assets/icon-tools/ugc-generator.webp" alt="UGC Generator" className="w-12 h-12 object-contain" width={48} height={48} />,
  },
];

const TOTAL_FRAMES = 246;
const FRAME_BASE_PATH = '/assets/animasi/ezgif-frame-';

// --- FeatureConnectivity Component Logic (Inlined) ---
const iconPath = '/assets/icon-tools/';
const featureIcons = {
  socialMedia: `${iconPath}media-generator.webp`,
  tts: `${iconPath}text-to-speech.webp`,
  imageToVideo: `${iconPath}video-generator.webp`,
  adsGenerator: `${iconPath}ads-generator.webp`,
  imageEditing: `${iconPath}image-editor.webp`,
  briefToImages: `${iconPath}image-generator.webp`,
  brandingGenerator: `${iconPath}branding-generator.webp`,
  contentPlanner: `${iconPath}content-planner.webp`,
  magicAi: `${iconPath}magic-ai.webp`,
  ugcGenerator: `${iconPath}ugc-generator.webp`,
};

const featureKeys = Object.keys(featureIcons);

const FeatureConnectivity = () => {
  const { t } = useTranslation();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const containerRef = useRef(null);
  const logoRef = useRef(null);

  // Responsive Logic
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const circleRadius = isMobile ? 120 : 200; // Radius lingkaran responsif

  // Konfigurasi posisi Icon melingkar
  const getPosition = (index, total) => {
    const angle = (index / total) * 360;
    const rad = (angle - 90) * (Math.PI / 180); // Start from top (-90 deg)
    return {
      x: Math.cos(rad) * circleRadius,
      y: Math.sin(rad) * circleRadius,
      angle
    };
  };

  useGSAP(() => {
    // SINGLE TIMELINE untuk Sinkronisasi Sempurna
    const tl = gsap.timeline({ repeat: -1 });

    // IMPORTANT: Set initial state Logo jadi Grayscale (Abu-abu) dulu
    gsap.set(logoRef.current, { filter: 'grayscale(100%)' });

    // 1. Animasi Aliran Data (Kabel)
    tl.fromTo('.flowing-cable',
      { strokeDashoffset: 60 },
      {
        strokeDashoffset: -300,
        duration: 2.0, // Durasi jalan 2 detik
        ease: "power1.inOut"
      },
      0 // Start di detik ke-0 relatif timeline
    );

    // 2. Animasi Logo (Sinkron)
    // Logo berubah jadi Biru saat kabel sampai
    // Hitungan timer: (Travel 260px / Total 360px) * 2.0s = 1.44s
    tl.to(logoRef.current, {
      filter: 'grayscale(0%)',
      duration: 0.5,
      ease: "power2.out"
    }, 1.45); // Insert di detik 1.45

    // Logo kembali Grayscale pelan-pelan setelah jadi biru
    tl.to(logoRef.current, {
      filter: 'grayscale(100%)',
      duration: 1.5,
      ease: "power2.inOut"
    });

  }, { scope: containerRef });

  return (
    <div ref={containerRef} className={`relative mx-auto flex items-center justify-center overflow-visible transition-all duration-300 ${isMobile ? 'w-[320px] h-[320px]' : 'w-[600px] h-[500px]'}`}>
      {/* Background Glow */}
      <div className="absolute inset-0 bg-radial-gradient from-blue-50/50 to-transparent pointer-events-none rounded-full" />

      {/* Pusat: NEO Logo (Inline SVG) */}
      <div className="absolute z-20 flex flex-col items-center justify-center bg-transparent drop-shadow-2xl w-20 h-20 md:w-32 md:h-32" ref={logoRef}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 328.37 328.37" className="w-full h-full">
          <g data-name="Layer 1">
            <rect width="328.37" height="328.37" rx="109.63" ry="109.63" fill="#00a4ff" />
            <g fill="#fff">
              <path d="M54.99 237.74c5 7.37 10.76 14.26 17.14 20.49 6 5.81 13.93 9.02 22.38 9.02s16.67-3.35 22.66-9.35l28.8-28.89c1.84-1.79 1.89-4.86.05-6.7l-7.98-8.26c-.9-.9-2.08-1.46-3.4-1.46-1.32-.05-2.5.47-3.45 1.37L102.2 243c-4.25 4.25-11.19 4.3-15.53.05a110.7 110.7 0 0 1-14.35-17.14c-12.42-18.27-18.93-39.66-18.93-61.8s6.56-43.58 18.93-61.85c4.2-6.23 9.06-11.99 14.45-17.23 4.25-4.15 11.19-4.11 15.44.14L221.8 204.76a9.83 9.83 0 0 0 7.27 2.88c2.69-.14 5.1-1.27 6.85-3.26 9.44-11 11.85-30.45 11.85-40.27 0-22.05-8.78-38.86-13.22-45.09-.8-1.13-1.94-2.03-3.35-2.22-1.37-.24-2.79.19-3.87 1.09l-8.36 8.5c-1.7 1.46-2.17 3.87-1.09 5.85.71 1.32 4.25 6.94 6.61 15.49 1.27 4.67 2.22 10.2 2.12 16.38-.05 7.27-1.42 13.46-1.61 14.16L117.03 70.3c-6.04-6.04-14.07-9.35-22.66-9.35s-16.38 3.21-22.33 9.02C65.67 76.2 59.86 83.09 54.9 90.41c-14.78 21.76-22.57 47.26-22.57 73.65s7.79 51.89 22.57 73.6l.09.09Z" />
              <path d="M273.53 90.49c-5-7.37-10.76-14.26-17.14-20.44-6-5.81-13.93-9.02-22.33-9.02s-16.67 3.35-22.71 9.35l-28.8 28.89c-1.84 1.79-1.89 4.86-.09 6.7l7.98 8.26c.9.9 2.08 1.46 3.4 1.46 1.32.05 2.5-.47 3.4-1.37l28.99-29.08c4.25-4.25 11.19-4.25 15.53-.05 5.33 5.19 10.15 10.95 14.35 17.14 12.37 18.27 18.93 39.66 18.93 61.8s-6.52 43.58-18.93 61.85c-4.2 6.18-9.06 11.99-14.45 17.23-4.3 4.15-11.19 4.11-15.44-.14L106.63 123.48c-1.94-1.94-4.58-3.02-7.27-2.88-2.64.14-5.1 1.27-6.85 3.26-9.44 11-11.85 30.45-11.85 40.27 0 22.05 8.83 38.86 13.22 45.09.85 1.13 1.94 2.03 3.35 2.22 1.42.19 2.79-.19 3.87-1.09l8.36-8.45c1.7-1.46 2.17-3.87 1.09-5.85-.71-1.32-4.25-6.94-6.61-15.49-1.27-4.67-2.22-10.2-2.17-16.38.09-7.27 1.46-13.46 1.61-14.16l107.97 107.97c6.04 6.04 14.07 9.35 22.66 9.35s16.38-3.21 22.33-9.02c6.37-6.23 12.18-13.12 17.14-20.49 14.78-21.76 22.57-47.21 22.57-73.65s-7.79-51.89-22.57-73.65l.05-.05Z" />
            </g>
          </g>
        </svg>
      </div>

      {/* SVG Layer untuk Kabel - Centered ViewBox */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible"
        viewBox={isMobile ? "-180 -180 360 360" : "-300 -300 600 600"}
        preserveAspectRatio="xMidYMid slice"
      >
        <g>
          {featureKeys.map((key, index) => {
            const { x, y } = getPosition(index, featureKeys.length);
            return (
              <React.Fragment key={key}>
                {/* Layer 1: Background Kabel (Pipa Redup) */}
                <path
                  d={`M ${x} ${y} L 0 0`}
                  fill="none"
                  stroke="#E2E8F0"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="opacity-50"
                />

                {/* Layer 2: Animasi Data (Packet Signal) */}
                <path
                  d={`M ${x} ${y} L 0 0`}
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="60 300"
                  className="flowing-cable opacity-100 will-change-[stroke-dashoffset]"
                />
              </React.Fragment>
            );
          })}
        </g>
      </svg>

      {/* Ikon Melingkar */}
      <div className="absolute inset-0 z-30 pointer-events-none">
        <div className="relative w-full h-full flex items-center justify-center">
          {featureKeys.map((key, index) => {
            const { x, y } = getPosition(index, featureKeys.length);
            return (
              <div
                key={key}
                className="absolute cursor-pointer pointer-events-auto transform transition-transform hover:scale-110 duration-300 group"
                style={{
                  transform: `translate(${x}px, ${y}px)`
                }}
                onClick={() => setSelectedFeature(key)}
              >
                <div className="relative w-12 h-12 md:w-20 md:h-20 bg-white rounded-2xl shadow-lg border border-blue-50 p-2 md:p-3 flex items-center justify-center group-hover:border-blue-400 group-hover:shadow-blue-200 transition-all">
                  <img
                    src={featureIcons[key]}
                    alt={t(`features.${key}.title`)}
                    className="w-full h-full object-contain rounded-xl"
                    width={80}
                    height={80}
                  />
                  {/* Tooltip Label */}
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 text-white text-[10px] px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none hidden md:block">
                    {t(`features.${key}.title`)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* MODAL DESKRIPSI */}
      {selectedFeature && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setSelectedFeature(null)}>
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 relative transform transition-all scale-100"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedFeature(null)}
              className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
            >
              <X size={20} className="text-slate-600" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 mb-6">
                <img
                  src={featureIcons[selectedFeature]}
                  alt={t(`features.${selectedFeature}.title`)}
                  className="w-full h-full object-contain drop-shadow-lg"
                  width={96}
                  height={96}
                />
              </div>
              <h3 className="text-3xl font-bold text-slate-800 mb-3">
                {t(`features.${selectedFeature}.title`)}
              </h3>
              <div className="w-16 h-1 bg-blue-500 rounded-full mb-6"></div>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t(`features.${selectedFeature}.desc`)}
              </p>

              <button
                onClick={() => setSelectedFeature(null)}
                className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-blue-500/30"
              >
                {t('authModal.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PricingCardWidget = ({ plan, features, isPopular, t, onAuthAction, checkingStatus }) => {
  const hasDiscount = plan.original_price && Number(plan.original_price) > Number(plan.price);
  const [isRevealed, setIsRevealed] = useState(!hasDiscount);
  const [displayPrice, setDisplayPrice] = useState(hasDiscount ? Number(plan.original_price) : Number(plan.price));

  const handleCheckDiscount = () => {
    setIsRevealed(true);
    const start = Number(plan.original_price);
    const end = Number(plan.price);
    const duration = 1500;
    const startTime = performance.now();

    const animate = (time) => {
      const timeFraction = (time - startTime) / duration;
      if (timeFraction >= 1) {
        setDisplayPrice(end);
        return;
      }
      const progress = 1 - Math.pow(1 - timeFraction, 4);
      setDisplayPrice(start - (start - end) * progress);
      requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  const formattedPrice = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(displayPrice);

  const formattedOriginalKey = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(plan.original_price || 0);

  return (
    <div className={`
      pricing-card relative rounded-3xl p-4 transition-all duration-300 flex flex-col border
      flex-shrink-0 w-[280px] snap-center
      ${isPopular
        ? 'border-[#2299ED] shadow-lg shadow-blue-200 bg-gradient-to-b from-[#F6F9FC] to-white hover:-translate-y-1'
        : 'border-[#CCD9E3] shadow-md shadow-slate-200 hover:-translate-y-1 bg-white'
      }
    `}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2299ED] text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-wide shadow-md uppercase">
          {t('pricing.popular')}
        </div>
      )}

      <div className="text-center mb-4 md:mb-5">
        <h3 className="text-base font-bold text-[#164361] mb-1">
          {plan.name}
        </h3>
        <div className="flex flex-col items-center justify-center gap-1 min-h-[64px]">
          {hasDiscount && isRevealed ? (
            <span className="text-xs text-[#475569] line-through animate-[fadeIn_0.5s_ease-out]">
              {formattedOriginalKey}
            </span>
          ) : (
            hasDiscount && <div className="h-4"></div>
          )}

          <span className={`text-2xl font-extrabold transition-colors duration-300 ${isPopular ? 'text-[#2299ED]' : 'text-[#164361]'}`}>
            {formattedPrice}
          </span>

          {hasDiscount && !isRevealed && (
            <button
              onClick={handleCheckDiscount}
              className="text-[10px] font-bold bg-green-100 text-green-700 px-3 py-1 rounded-full hover:bg-green-200 transition-colors animate-pulse cursor-pointer mt-1 flex items-center gap-1 mx-auto"
            >
              <TicketPercent size={12} />
              Cek Diskon
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 md:mb-6 grow">
        <p className="text-[10px] font-bold text-[#475569] mb-2 uppercase tracking-wide">
          {t('pricing.featuresLabel')}
        </p>
        <ul className="space-y-1">
          {plan.is_custom_button && plan.custom_features ? (
            plan.custom_features.split(',').map((txt, idx) => (
              <li key={idx} className="flex items-center text-[#164361] text-xs">
                <span className="w-4 h-4 mr-2 text-[#2299ED] bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-[8px]">✓</span>
                {txt.trim()}
              </li>
            ))
          ) : (
            features.map((feature, idx) => (
              <li key={idx} className="flex items-center text-[#164361] text-xs">
                <span className="w-4 h-4 mr-2 text-[#2299ED] bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-[8px]">✓</span>
                {t(`features.${feature.key}.title`, feature.name)}
              </li>
            ))
          )}
        </ul>
      </div>

      {plan.is_custom_button ? (
        <a
          href={plan.button_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`block w-full py-2 rounded-xl font-bold text-sm text-center transition-all duration-300 mt-auto
          bg-[#164361] text-white hover:bg-[#2299ED] shadow-md shadow-blue-200`}
        >
          {plan.button_text || t('pricing.contactButton')}
        </a>
      ) : (
        <button
          onClick={onAuthAction}
          disabled={checkingStatus}
          className={`w-full py-2 rounded-xl font-bold text-sm transition-all duration-300 mt-auto disabled:opacity-70
          ${isPopular
              ? 'bg-[#2299ED] text-white hover:bg-[#164361] shadow-md shadow-blue-200'
              : 'bg-[#164361] text-white hover:bg-[#2299ED] shadow-md shadow-blue-200'
            }`}
        >
          {checkingStatus ? t('pricing.ctaChecking') : Number(plan.price) === 0 ? t('pricing.tryButton') : t('pricing.buyButton', { name: plan.name })}
        </button>
      )}
    </div>
  );
};


// --- BRAND MARQUEE COMPONENT (INLINE) ---
// --- BRAND MARQUEE COMPONENT (INLINE) ---
const footerLogos = [
  { file: "alibaba.webp", w: 256, h: 29 },
  { file: "blackdorestlabs.webp", w: 256, h: 63 },
  { file: "bria.webp", w: 256, h: 165 },
  { file: "bytedance.png", w: 362, h: 62 },
  { file: "google.webp", w: 256, h: 84 },
  { file: "highfield.png", w: 286, h: 56 },
  { file: "klingai.webp", w: 248, h: 53 },
  { file: "lighttricks.svg", w: 142, h: 31 },
  { file: "minimax.webp", w: 256, h: 54 },
  { file: "nightcafe.png", w: 244, h: 88 },
  { file: "openai.webp", w: 256, h: 70 },
  { file: "openart.png", w: 269, h: 44 },
  { file: "pixverse.webp", w: 256, h: 48 },
  { file: "runway.webp", w: 256, h: 65 }
];

const BrandMarquee = () => (
  <div className="w-full bg-white py-10 overflow-hidden">
    <style>
      {`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-infinite {
            animation: marquee 120s linear infinite;
            display: flex;
            width: max-content;
          }
          .animate-marquee-infinite:hover {
            animation-play-state: paused;
          }
        `}
    </style>

    <div className="w-full overflow-hidden relative">
      <div className="animate-marquee-infinite items-center px-4">
        {[...footerLogos, ...footerLogos].map((logo, index) => (
          <div key={index} className="flex-shrink-0 mx-6 md:mx-10 flex items-center justify-center group cursor-pointer h-12">
            <img
              src={`/assets/assets-footer/${logo.file}`}
              alt={logo.file.split('.')[0]}
              className="h-10 w-auto object-contain filter invert opacity-60 hover:opacity-100 transition-all duration-300"
              loading="lazy"
              decoding="async"
              width={logo.w}
              height={logo.h}
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Typewriter = ({ text, className = "", tag = "span", speed = 0.02, delay = 0 }) => {
  const el = useRef(null);

  useGSAP(() => {
    const chars = el.current.querySelectorAll(".char");
    gsap.fromTo(chars,
      { opacity: 0.2 },
      {
        opacity: 1,
        stagger: speed,
        delay: delay,
        ease: "none",
        scrollTrigger: {
          trigger: el.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, { scope: el, dependencies: [text] });

  const Component = tag;
  const splitText = text.split("").map((char, i) => (
    <span key={i} className="char opacity-20 transition-opacity duration-300">
      {char}
    </span>
  ));

  return (
    <Component ref={el} className={className}>
      {splitText}
    </Component>
  );
};



// --- FAQ COMPONENTS (INTEGRATED) ---
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
        onClick={onClick}
      >
        <span className={`text-lg font-medium transition-colors duration-200 ${isOpen ? 'text-[#2299ED]' : 'text-slate-900 group-hover:text-[#2299ED]'}`}>
          {question}
        </span>
        <span className={`ml-6 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
          {isOpen ? <ChevronUp size={20} className="text-[#2299ED]" /> : <ChevronDown size={20} className="text-slate-500 group-hover:text-[#2299ED]" />}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'}`}
      >
        <p className="text-slate-600 leading-relaxed text-base">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  // State changed to an array to support multiple open items.
  // Initialized as empty [] so all are closed by default.
  const [openIndexes, setOpenIndexes] = useState([]);

  const toggleIndex = (index) => {
    setOpenIndexes(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index); // Close if already open
      } else {
        return [...prev, index]; // Open if closed
      }
    });
  };

  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
  ];

  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Decorations (Subtle for white theme) */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#2299ED] rounded-full blur-[128px] opacity-[0.03] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-[128px] opacity-[0.03] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('faq.title')}
          </h2>
          <p className="text-slate-500 text-lg">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 border border-slate-100 shadow-xl shadow-slate-200/50">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndexes.includes(index)}
              onClick={() => toggleIndex(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};



const CallToAction = ({ onAuthAction }) => {
  const { t } = useTranslation();

  return (
    <section className="cta-section py-24 relative overflow-hidden bg-gradient-to-br from-[#2299ED] to-[#164361]">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white rounded-full blur-[120px] opacity-[0.15] translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#8BC8EE] rounded-full blur-[120px] opacity-[0.15] -translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 relative z-10 text-center text-white">
        <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight tracking-tight">
          {t('ctaSection.title')}
        </h2>
        <p className="text-lg md:text-2xl text-blue-50 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
          {t('ctaSection.subtitle')}
        </p>
        <div className="flex justify-center">
          <button
            onClick={onAuthAction}
            className="group relative inline-flex items-center gap-3 px-10 py-5 bg-white text-[#164361] rounded-full font-bold text-lg md:text-xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_50px_rgba(255,255,255,0.2)] transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 active:scale-95"
          >
            <span className="relative z-10">{t('ctaSection.button')}</span>
            <ArrowRight size={24} className="relative z-10 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 rounded-full bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>
    </section>
  );
};

const UseCasesSection = () => {
  const { t } = useTranslation();
  const container = useRef(null);

  useGSAP(() => {
    gsap.from(container.current, {
      scrollTrigger: {
        trigger: container.current,
        start: "top 80%",
      },
      y: 50,
      opacity: 0,
      duration: 1,
      ease: "power2.out"
    });
  }, { scope: container });

  const headingFont = {
    fontFamily: '"Special Gothic Expanded One", "Kohinoor Devanagari", system-ui, sans-serif',
  };

  const cases = [
    "agency", "startup", "creator", "marketing", "ecommerce", "developer", "business", "education"
  ];

  return (
    <section ref={container} className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-[#164361] mb-6" style={headingFont}>
            {t('useCases.title')}
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            {t('useCases.subtitle')}
          </p>
        </div>
      </div>

      {/* Static Grid Container */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 px-2 max-w-7xl mx-auto">
        {cases.map((key, idx) => (
          <div
            key={`${key}-${idx}`}
            className="p-4 md:p-5 rounded-2xl bg-[#F0F9FF] border border-blue-50 flex flex-col justify-start text-left transition-all duration-300 hover:shadow-lg hover:border-blue-200 hover:-translate-y-1 hover:bg-[#E6F4FF]"
          >
            <h3 className="text-base md:text-lg font-bold text-[#00a4ff] mb-2 leading-tight" style={headingFont}>
              {t(`useCases.${key}Title`)}
            </h3>
            <p className="text-[10px] md:text-xs text-[#164361] font-medium leading-relaxed opacity-80">
              {t(`useCases.${key}Desc`)}
            </p>
          </div>
        ))}
      </div>
    </section >
  );
};

const ComparisonSection = () => {
  const { t } = useTranslation();
  const container = useRef(null);

  useGSAP(() => {
    gsap.from(".comparison-card", {
      scrollTrigger: {
        trigger: container.current,
        start: "top 80%",
      },
      y: 50,
      opacity: 0,
      stagger: 0.2,
      duration: 1,
      ease: "power3.out"
    });
  }, { scope: container });

  const headingFont = {
    fontFamily: '"Special Gothic Expanded One", "Kohinoor Devanagari", system-ui, sans-serif',
  };

  const rows = [
    { key: "row1" },
    { key: "row2" },
    { key: "row3" },
    { key: "row4" },
    { key: "row5" },
    { key: "row6" },
  ];

  return (
    <section ref={container} className="py-16 bg-[#F8FAFC] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[-10%] w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[100px]" />
        <div className="absolute bottom-[10%] right-[-10%] w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-bold text-[#164361] mb-6" style={headingFont}>
            {t('comparison.title')}
          </h2>
          <p className="text-xl text-slate-500 max-w-3xl mx-auto leading-relaxed">
            {t('comparison.subtitle')}
          </p>
        </div>

        {/* DESKTOP TABLE */}
        <div className="hidden md:grid grid-cols-4 items-stretch rounded-3xl overflow-hidden shadow-xl bg-white border border-slate-200">
          {/* Header Row */}
          <div className="col-span-1 bg-slate-50 p-4 flex items-center justify-center border-b border-r border-slate-100">
            <span className="text-slate-500 font-bold tracking-widest text-xs">COMPARISON</span>
          </div>
          <div className="col-span-1 p-4 text-center bg-slate-50 border-b border-r border-slate-100">
            <h3 className="text-base font-bold text-slate-600 mb-1">{t('comparison.col1')}</h3>
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Traditional</span>
          </div>
          <div className="col-span-1 p-4 text-center bg-slate-50 border-b border-r border-slate-100">
            <h3 className="text-base font-bold text-slate-600 mb-1">{t('comparison.col2')}</h3>
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Generic AI</span>
          </div>
          <div className="col-span-1 p-4 text-center bg-[#00a4ff] border-b border-slate-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-white text-[#0077CC] text-[9px] font-bold px-2 py-0.5 rounded-bl-lg shadow-sm z-10">
              RECOMMENDED
            </div>
            {/* User suggested gradient as a glow effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%]"
              style={{ background: 'radial-gradient(circle, #00a4ff 0%, #00a4ff 85%, transparent 100%)', opacity: 0.5, filter: 'blur(20px)' }}
            ></div>

            <h3 className="text-lg font-bold text-white mb-0.5 relative z-10">{t('comparison.col3')}</h3>
            <span className="text-[10px] text-blue-50 uppercase tracking-wide relative z-10">The Best Choice</span>
          </div>

          {/* Data Rows */}
          {rows.map((row, idx) => (
            <React.Fragment key={idx}>
              <div className="p-4 bg-white flex items-center font-bold text-slate-700 border-b border-r border-slate-100/50 text-sm">
                {t(`comparison.${row.key}`)}
              </div>

              <div className="p-4 flex flex-col items-center justify-center text-center border-b border-r border-slate-100/50 bg-slate-50/50">
                <X className="text-slate-300 w-5 h-5 mb-1" />
                <span className="text-xs font-medium text-slate-500 max-w-[150px] leading-tight">
                  {t(`comparison.${row.key}_cell1`)}
                </span>
              </div>

              <div className="p-4 flex flex-col items-center justify-center text-center border-b border-r border-slate-100/50 bg-slate-50/50">
                <Minus className="text-slate-300 w-5 h-5 mb-1" />
                <span className="text-xs font-medium text-slate-500 max-w-[150px] leading-tight">
                  {t(`comparison.${row.key}_cell2`)}
                </span>
              </div>

              <div className={`p-4 flex flex-col items-center justify-center text-center bg-[#f0f9ff] border-b border-[#00a4ff]/10 relative z-10`}>
                <Check className="text-[#00a4ff] w-6 h-6 mb-1 drop-shadow-sm" strokeWidth={4} />
                <span className="text-xs font-bold text-[#164361] max-w-[150px] leading-tight">
                  {t(`comparison.${row.key}_cell3`)}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* MOBILE CARDS */}
        <div className="md:hidden flex flex-col gap-6">
          {/* Freelancer Card (First) */}
          <div className="comparison-card bg-slate-50 rounded-2xl p-6 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-500 mb-4 text-center">{t('comparison.col1')}</h3>
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div key={idx} className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <span className="text-slate-500">{t(`comparison.${row.key}`)}</span>
                  <span className="text-slate-500">{t(`comparison.${row.key}_cell1`)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Other AI Card (Second) */}
          <div className="comparison-card bg-white rounded-2xl p-6 shadow-md border border-slate-200">
            <h3 className="text-lg font-bold text-slate-600 mb-4 text-center">{t('comparison.col2')}</h3>
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div key={idx} className="flex justify-between text-sm py-2 border-b border-slate-50 last:border-0">
                  <span className="text-slate-500">{t(`comparison.${row.key}`)}</span>
                  <span className="text-slate-600 font-medium">{t(`comparison.${row.key}_cell2`)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Neo AI Card (Highlighted - Last/Winner) */}
          <div className="comparison-card rounded-3xl p-8 shadow-2xl relative overflow-hidden" style={{ background: '#00a4ff' }}>
            <div className="absolute top-0 right-0 bg-white text-[#00a4ff] text-xs font-extrabold px-4 py-1.5 rounded-bl-2xl">
              WINNER
            </div>
            <h3 className="text-2xl font-bold text-white mb-8 text-center tracking-tight">{t('comparison.col3')}</h3>

            <div className="space-y-6">
              {rows.map((row, idx) => (
                <div key={idx} className="flex justify-between items-center border-b border-white/20 pb-4 last:border-0 last:pb-0">
                  <span className="text-blue-100 text-sm font-medium">{t(`comparison.${row.key}`)}</span>
                  <span className="text-white font-bold text-right text-base">{t(`comparison.${row.key}_cell3`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const providerModels = [
  { file: "Flux.1 [schnell].webp", name: "FLUX.1 [schnell]" },
  { file: "Flux.2 [dev].webp", name: "FLUX.2 [dev]" },
  { file: "seedance pro fast.webp", name: "Seedance 1.0 Pro" },
  { file: "nano banana.webp", name: "Nano Banana 2" },
  { file: "kling ai.webp", name: "Wan 2.5 Preview" },
  { file: "hidream.webp", name: "HiDream" },
  { file: "imagen 4 fast.webp", name: "Imagen 3 Fast" },
  { file: "mini max.webp", name: "MiniMax" },
  { file: "pixverselipsync.webp", name: "PixVerse LipSync" },
  { file: "qwen-image-edit.webp", name: "Qwen Edit" },
  { file: "vidu Q1.webp", name: "Vidu Q1" },
  { file: "seededit 3.0.webp", name: "SeedEdit 3.0" },
  { file: "imagen.webp", name: "Imagen 3 Pro" },
  { file: "nanobanana pro.png", name: "Nano Banana Pro" },
  { file: "qwen image.webp", name: "Qwen Image" },
  { file: "seedance pro.webp", name: "Seedance Pro" },
  { file: "seedance.webp", name: "Seedance" },
  { file: "seedrean 3.0.webp", name: "Seedream 3.0" },
];

const ModelsMarquee = React.memo(() => {
  const { t } = useTranslation();

  // Define the 6 Groups of 3 items each
  const groups = [
    // Group 1: Pattern A (Top Split)
    { type: 'A', items: [providerModels[0], providerModels[1], providerModels[12]] }, // Flux 1, Flux 2, Imagen Pro
    // Group 2: Pattern B (Bottom Split)
    { type: 'B', items: [providerModels[3], providerModels[13], providerModels[9]] }, // Nano 2, Nano Pro, Qwen Edit
    // Group 3: Pattern A (Top Split)
    { type: 'A', items: [providerModels[5], providerModels[7], providerModels[4]] }, // HiDream, MiniMax, Wan
    // Group 4: Pattern B (Bottom Split)
    { type: 'B', items: [providerModels[2], providerModels[8], providerModels[10]] }, // Seedance Fast, PixVerse, Vidu
    // Group 5: Pattern A (Top Split)
    { type: 'A', items: [providerModels[14], providerModels[11], providerModels[17]] }, // Qwen, SeedEdit, Seedream
    // Group 6: Pattern B (Bottom Split)
    { type: 'B', items: [providerModels[6], providerModels[16], providerModels[15]] }, // Imagen Fast, Seedance, Seedance Pro
  ];

  const CardContent = ({ model, isSmall = false }) => (
    <>
      <img
        src={`/assets/assets-provider/${model.file}`}
        alt={model.name}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        width={isSmall ? 292 : 600}
        height={isSmall ? 292 : 600}
      />
      <div className="absolute bottom-0 left-0 p-3 w-full z-10 pointer-events-none">
        <div className={`bg-white/95 backdrop-blur-md inline-block px-2 py-1 rounded-lg ${isSmall ? 'text-[10px]' : 'text-xs'} font-bold text-[#00a4ff] shadow-sm border border-blue-100 max-w-full truncate`}>
          {model.name}
        </div>
      </div>
    </>
  );

  return (
    <section className="py-20 bg-slate-50 border-y border-slate-200 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-12 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-[#164361] mb-2" style={{ fontFamily: '"Special Gothic Expanded One", sans-serif' }}>
          {t('modelsMarquee.title')}
        </h2>
        <p className="text-slate-500">{t('modelsMarquee.subtitle')}</p>
      </div>

      <style>{`
        @keyframes marquee-grid {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-33.333333%, 0, 0); }
        }
        .animate-marquee-grid {
          animation: marquee-grid 100s linear infinite;
          will-change: transform;
        }
      `}</style>

      <div className="relative w-full overflow-hidden h-[940px] bg-slate-50">
        <div className="flex w-max animate-marquee-grid">
          {/* Loop 3 times to ensure infinite seamless scrolling without blank gaps */}
          {[0, 1, 2].map((iter) => (
            <div
              key={iter}
              className="grid grid-cols-12 gap-4 mx-2 flex-shrink-0"
              style={{ width: '3680px', height: '908px', gridTemplateRows: '292px 292px 292px' }}
            >
              {groups.map((group, gIdx) => {
                // Calculate Column Start based on Group Index (0-5)
                // Group 0 -> Col 1 (+2 cols)
                // Group 1 -> Col 3
                // Group 5 -> Col 11
                const startCol = (gIdx * 2) + 1;

                if (group.type === 'A') {
                  // Pattern A (Top Split):
                  // Sm1: Row 1, Col Start
                  // Sm2: Row 1, Col Start+1
                  // Big: Row 2, Col Start, Span 2x2
                  return (
                    <React.Fragment key={`g-${gIdx}`}>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol, gridRowStart: 1, gridColumnEnd: 'span 1', gridRowEnd: 'span 1' }}>
                        <CardContent model={group.items[0]} isSmall={true} />
                      </div>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol + 1, gridRowStart: 1, gridColumnEnd: 'span 1', gridRowEnd: 'span 1' }}>
                        <CardContent model={group.items[1]} isSmall={true} />
                      </div>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol, gridRowStart: 2, gridColumnEnd: 'span 2', gridRowEnd: 'span 2' }}>
                        <CardContent model={group.items[2]} />
                      </div>
                    </React.Fragment>
                  );
                } else {
                  // Pattern B (Bottom Split):
                  // Big: Row 1, Col Start, Span 2x2
                  // Sm1: Row 3, Col Start
                  // Sm2: Row 3, Col Start+1
                  return (
                    <React.Fragment key={`g-${gIdx}`}>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol, gridRowStart: 1, gridColumnEnd: 'span 2', gridRowEnd: 'span 2' }}>
                        <CardContent model={group.items[0]} />
                      </div>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol, gridRowStart: 3, gridColumnEnd: 'span 1', gridRowEnd: 'span 1' }}>
                        <CardContent model={group.items[1]} isSmall={true} />
                      </div>
                      <div className="relative rounded-2xl overflow-hidden group border border-slate-200 shadow-sm bg-white"
                        style={{ gridColumnStart: startCol + 1, gridRowStart: 3, gridColumnEnd: 'span 1', gridRowEnd: 'span 1' }}>
                        <CardContent model={group.items[2]} isSmall={true} />
                      </div>
                    </React.Fragment>
                  );
                }
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

const HomePage = () => {
  const { t } = useTranslation();
  const container = useRef();
  const navigate = useNavigate();
  const [dbPlans, setDbPlans] = useState([]);
  const [packageFeatures, setPackageFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [activeFeature, setActiveFeature] = useState('magicAi');

  const headingFont = {
    fontFamily:
      '"Special Gothic Expanded One", "Kohinoor Devanagari", system-ui, sans-serif',
  };

  const bodyFont = {
    fontFamily:
      '"Kohinoor Devanagari", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const typedSuperAI = useTypingEffect(t('hero.title1'), 140, 1400);

  // 🔥 Canvas untuk animasi hero
  const { canvasRef: heroCanvasRef, isLoaded: heroImagesLoaded } = useImageSequenceCanvas(
    TOTAL_FRAMES,
    FRAME_BASE_PATH,
    24 // fps
  );

  // --- 1. FETCH PAKET KREDIT ---
  useEffect(() => {
    async function fetchCreditPackages() {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('credit_packages')
        .select('id, name, price, base_credits, populer, beranda, original_price, is_custom_button, button_text, button_url, custom_features')
        .eq('is_active', true)
        .eq('beranda', true)
        .order('price', { ascending: true });

      if (error) {
        console.error('Error fetching packages:', error);
        setError('Gagal memuat paket: ' + error.message);
        setDbPlans([]);
      } else {
        setDbPlans(data || []);
      }

      setLoading(false);
    }

    fetchCreditPackages();
  }, []);

  // --- 1b. FETCH FITUR PER PAKET ---
  useEffect(() => {
    const fetchPackageFeatures = async () => {
      if (!dbPlans || dbPlans.length === 0) {
        setPackageFeatures({});
        return;
      }

      const packageIds = dbPlans.map((p) => p.id);

      const { data: pfRows, error: pfError } = await supabase
        .from('package_features')
        .select('package_id, feature:app_features!feature_id(slug, name, description)')
        .in('package_id', packageIds);

      if (pfError) {
        console.error('Error fetching package_features:', pfError);
        setPackageFeatures({});
        return;
      }

      const map = {};
      (pfRows || []).forEach((row) => {
        if (!row.feature) return;
        if (!map[row.package_id]) map[row.package_id] = [];
        map[row.package_id].push(row.feature);
      });

      setPackageFeatures(map);
    };

    fetchPackageFeatures();
  }, [dbPlans]);

  // --- 2. CEK STATUS AUTH + PROFIL ---
  const checkAuthStatus = async () => {
    setCheckingStatus(true);
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      setIsLoggedIn(true);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('phone')
        .eq('id', session.user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
        setIsProfileComplete(false);
      } else if (profile && profile.phone) {
        setIsProfileComplete(true);
      } else {
        setIsProfileComplete(false);
      }
    } else {
      setIsLoggedIn(false);
      setIsProfileComplete(false);
    }
    setCheckingStatus(false);
  };

  useEffect(() => {
    checkAuthStatus();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          checkAuthStatus();
        } else {
          setIsLoggedIn(false);
          setIsProfileComplete(false);
        }
      }
    );

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // --- 3. HANDLE CTA UTAMA ---
  const handleAuthAction = () => {
    if (checkingStatus) return;

    if (isLoggedIn && isProfileComplete) {
      navigate('/dashboard');
    } else if (isLoggedIn && !isProfileComplete) {
      navigate('/login');
    } else {
      setShowLoginModal(true);
    }
  };

  // --- 4. LOGIN GOOGLE ---
  const handleGoogleLogin = async () => {
    setCheckingStatus(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/login',
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error('Google Login Error:', error);
      setCheckingStatus(false);
    }
  };

  // --- 5. ANIMASI GSAP (Hero + Section + Underline) ---
  useGSAP(
    () => {
      gsap.set('.hero-title', { y: 20, opacity: 0 });
      gsap.set('.hero-desc', { y: 20, opacity: 0 });
      gsap.set('.hero-btn', { y: 20, opacity: 0 });
      gsap.set('.feature-card, .cta-content, .section-header', {
        opacity: 0,
        y: 50,
      });

      const tlHero = gsap.timeline({
        defaults: { duration: 0.8, ease: 'power3.out' },
      });

      tlHero
        .to('.hero-title', { y: 0, opacity: 1 })
        .to(
          '.hero-desc',
          { y: 0, opacity: 1, duration: 0.6 },
          '-=0.4'
        )
        .to(
          '.hero-btn',
          { y: 0, opacity: 1, stagger: 0.15, duration: 0.6 },
          '-=0.4'
        );

      gsap.to('.features-section .section-header', {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.features-section',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });

      gsap.to('.feature-card', {
        opacity: 1,
        y: 0,
        stagger: 0.1,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.features-section',
          start: 'top 70%',
          toggleActions: 'play none none none',
        },
      });

      gsap.to('.cta-content', {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });

      gsap.to('.underline-glow', {
        backgroundPositionX: '200%',
        duration: 1.8,
        ease: 'none',
        repeat: -1,
      });
    },
    { scope: container, dependencies: [] }
  );

  // --- 6. ANIMASI PRICING ---
  useGSAP(
    () => {
      gsap.set('.pricing-card', { opacity: 0, y: 50 });
      if (!loading && dbPlans.length > 0) {
        gsap.to('.pricing-section .section-header', {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.pricing-section',
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        });
        gsap.to('.pricing-card', {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.pricing-section',
            start: 'top 70%',
            toggleActions: 'play none none none',
          },
        });
      }
    },
    { scope: container, dependencies: [loading, dbPlans] }
  );

  // --- 7. MODAL OTENTIKASI ---
  const AuthModal = ({ show, onClose, onGoogleLogin, checkingStatus }) => {
    if (!show) return null;

    const handleAccountSelect = () => {
      onGoogleLogin();
    };

    return (
      <div
        className="fixed inset-0 backdrop-blur-sm bg-black/30 z-[100] flex items-center justify-center p-4 transition-all duration-300"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-sm transform transition-all duration-300 scale-100 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Image Full Width */}
          <div className="w-full h-48 bg-slate-50 relative">
            <img
              src="/assets/dashboard/login.png"
              alt="Login Illustration"
              className="w-full h-full object-cover"
              width={384}
              height={192}
            />
          </div>

          <div className="p-8 text-center pt-6">
            <h3
              className="text-2xl font-bold text-[#164361] mb-2"
              style={headingFont}
            >
              {t('authModal.title')}
            </h3>
            <p className="text-gray-500 mb-6 text-sm">
              {t('authModal.desc')}
            </p>

            <button
              onClick={handleAccountSelect}
              disabled={checkingStatus}
              className="w-full bg-[#2299ED] hover:bg-[#164361] text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-blue-300 disabled:opacity-70"
            >
              {checkingStatus ? (
                <>
                  <span className="animate-spin h-5 w-5 border-t-2 border-r-2 border-white rounded-full" />
                  {t('authModal.redirecting')}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#fff"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#fff"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#fff"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#fff"
                    />
                  </svg>
                  {t('authModal.googleBtn')}
                </>
              )}
            </button>

            <div className="mt-4 text-center">
              <button
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                {t('authModal.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- 8. RENDER PAGE ---
  return (
    <div
      ref={container}
      className="bg-[#F6F9FC] text-[#164361] overflow-x-hidden"
      style={bodyFont}
    >
      <Helmet>
        <title>Neo AI - Super AI for Every Generation</title>
        <meta name="description" content={t ? t('hero.desc') : 'Experience the power of boundless AI.'} />
        <link rel="canonical" href="https://neoai.id/" />
      </Helmet>
      <AuthModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onGoogleLogin={handleGoogleLogin}
        checkingStatus={checkingStatus}
      />

      {/* Main Content Wrapper for SEO */}
      <main>

        {/* HERO */}
        <section className="relative min-h-[calc(100vh-5rem)] py-16 md:py-0 flex items-center px-6 overflow-hidden bg-white" aria-label="Hero Section">
          <div className="absolute top-[-140px] right-[-160px] w-[460px] h-[460px] bg-[#164361] rounded-full blur-3xl opacity-70 -z-10" aria-hidden="true" />
          <div className="absolute bottom-[-160px] left-[-180px] w-[460px] h-[460px] bg-[#E5F2FF] rounded-full blur-3xl opacity-60 -z-10" aria-hidden="true" />

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* Kiri: Teks hero */}
            <div className="text-center md:text-left">
              <h1
                className="hero-title text-3xl sm:text-3xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-[#164361]"
                style={headingFont}
              >
                <span className="block">
                  <span className="text-[#2299ED]">
                    {typedSuperAI}
                    <span className="inline-block w-[3px] h-[1.1em] bg-[#2299ED] ml-1 align-middle animate-pulse" />
                  </span>{' '}
                </span>
                <span className="block gradient-text-fix mt-1">
                  {t('hero.title2')}
                </span>
              </h1>

              <p className="hero-desc text-base sm:text-lg text-[#475569] mb-8 max-w-xl mx-auto md:mx-0 leading-relaxed">
                {t('hero.desc')}
              </p>

              <div className="flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-4">
                <button
                  onClick={handleAuthAction}
                  disabled={checkingStatus}
                  aria-label={isLoggedIn && isProfileComplete ? t('hero.ctaDashboard') : t('hero.ctaPrimary')}
                  className="hero-btn w-full sm:w-auto px-8 py-3 sm:py-4 bg-[#0077CC] hover:bg-[#164361] text-white font-bold rounded-xl shadow-lg shadow-blue-200 transition duration-300 transform hover:-translate-y-1 flex items-center justify-center disabled:opacity-70"
                >
                  {checkingStatus
                    ? t('hero.ctaChecking')
                    : isLoggedIn && isProfileComplete
                      ? t('hero.ctaDashboard')
                      : t('hero.ctaPrimary')}
                </button>

                <Link
                  to="/about"
                  aria-label={t('hero.ctaSecondary')}
                  className="hero-btn w-full sm:w-auto px-8 py-3 sm:py-4 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-xl transition duration-300 hover:text-[#2299ED] flex items-center justify-center"
                >
                  {t('hero.ctaSecondary')}
                </Link>
              </div>
            </div>

            {/* Kanan: animasi dalam canvas */}
            <div className="flex justify-center md:justify-end mt-10 md:mt-0">
              <div
                className="
                relative w-full max-w-xl
                rounded-[32px] bg-white overflow-hidden
                h-[260px] sm:h-[320px] md:h-[380px]
              "
                role="img"
                aria-label="NeoAI Platform Animation - Platform AI dengan berbagai tools kreatif"
              >
                <canvas ref={heroCanvasRef} className="w-full h-full relative z-0" width={1200} height={720} />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-white/70 rounded-[32px]" />
              </div>
            </div>
          </div>
        </section>

        {/* Brand Marquee (Moved to Top for Trust) */}
        <BrandMarquee />

        {/* FEATURES */}
        <section className="features-section pt-20 pb-0 md:pt-24 md:pb-0 bg-[#F6F9FC] px-6" aria-label="Fitur-fitur NeoAI">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16 section-header">
              <div className="inline-block">
                <h2
                  className="text-3xl md:text-5xl font-bold text-[#164361]"
                  style={headingFont}
                >
                  {t('features.title')}
                </h2>
                <span
                  className="
                  underline-glow
                  block mt-3 h-[2px] w-full
                  rounded-full
                  bg-repeat-x
                  bg-[length:200%_100%]
                  bg-[linear-gradient(90deg,#164361,#2299ED,#8BC8EE,#164361)]
                "
                />
              </div>

              <div className="text-sm sm:text-base md:text-xl text-[#475569] max-w-2xl mx-auto mt-4 min-h-[3rem] px-4">
                <Typewriter text={t('features.subtitle')} speed={0.03} />
              </div>
            </div>

            {/* NEW CIRCULAR FEATURE CONNECTIVITY DIAGRAM */}
            <div className="w-full">
              <FeatureConnectivity />
            </div>
          </div>
        </section>

        {/* SHOWCASE SECTION: Magic AI Only + CTA */}
        <section className="pt-10 pb-20 bg-slate-50 overflow-hidden" id="showcase">
          <style>{`
                @keyframes scrollLeft {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }
                @keyframes scrollRight {
                    from { transform: translateX(-50%); }
                    to { transform: translateX(0); }
                }
                .marquee-container {
                    display: flex;
                    width: max-content;
                    will-change: transform;
                }
                .animate-scroll-left {
                    animation: scrollLeft 60s linear infinite;
                }
                .animate-scroll-right {
                    animation: scrollRight 60s linear infinite;
                }
                .pause-hover:hover .marquee-container {
                    animation-play-state: paused;
                }
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

          <div className="text-center mb-12 px-4">
            <h2 className="text-3xl md:text-5xl font-bold text-[#164361] mb-4" style={headingFont}>
              {t('showcase.title')}
            </h2>
            <div className="text-lg text-slate-600 max-w-2xl mx-auto min-h-[3rem]">
              <Typewriter text={t('showcase.subtitle')} speed={0.03} />
            </div>
          </div>

          {/* MARQUEE CONTENT (Magic AI Only) */}
          {(() => {
            const displayTools = magicAiTools;
            const row1 = displayTools.slice(0, Math.ceil(displayTools.length / 2));
            const row2 = displayTools.slice(Math.ceil(displayTools.length / 2));

            const handleItemClick = () => {
              if (!isLoggedIn) {
                navigate('/login');
              } else {
                navigate('/dashboard');
              }
            };

            return (
              <>
                {/* Row 1: Left Scroll */}
                <div className="mb-8 w-full pause-hover">
                  <div className="marquee-container animate-scroll-left">
                    {[...row1, ...row1].map((tool, idx) => (
                      <div key={`r1-${idx}`}
                        onClick={handleItemClick}
                        className="relative mx-2 md:mx-3 w-[120px] h-[150px] md:w-[150px] md:h-[190px] bg-white rounded-xl border border-slate-200 p-2 md:p-3 flex flex-col cursor-pointer hover:border-blue-400 transition-colors shrink-0"
                      >
                        <div className="relative w-full aspect-square bg-slate-100 rounded-lg overflow-hidden mb-2">
                          <img
                            src={`/assets/magic-ai/${tool.file}`}
                            alt={tool.label}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={400}
                          />
                          {!isLoggedIn && (
                            <div className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><Lock size={12} className="text-white" /></div>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-center text-slate-700 truncate">{tool.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 2: Right Scroll */}
                <div className="w-full pause-hover mb-12">
                  <div className="marquee-container animate-scroll-right">
                    {[...row2, ...row2].map((tool, idx) => (
                      <div key={`r2-${idx}`}
                        onClick={handleItemClick}
                        className="relative mx-2 md:mx-3 w-[120px] h-[150px] md:w-[150px] md:h-[190px] bg-white rounded-xl border border-slate-200 p-2 md:p-3 flex flex-col cursor-pointer hover:border-blue-400 transition-colors shrink-0"
                      >
                        <div className="relative w-full aspect-square bg-slate-100 rounded-lg overflow-hidden mb-2">
                          <img
                            src={`/assets/magic-ai/${tool.file}`}
                            alt={tool.label}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={400}
                          />
                          {!isLoggedIn && (
                            <div className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><Lock size={12} className="text-white" /></div>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-center text-slate-700 truncate">{tool.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* EXPLORE MORE CTA */}
                <div className="flex justify-center px-4">
                  <button
                    onClick={() => !isLoggedIn ? navigate('/login') : navigate('/dashboard')}
                    className="group relative px-8 py-4 bg-[#164361] hover:bg-[#2299ED] text-white font-bold rounded-full shadow-lg shadow-blue-200 transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {t('showcase.cta')}
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path></svg>
                    </span>
                    <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  </button>
                </div>
              </>
            );
          })()}
        </section>

        {/* NEW SECTION: MODELS PROVIDER (DOUBLE MARQUEE) - MOVED UP */}
        <ModelsMarquee />

        {/* NEW SECTION: USE CASES */}
        <UseCasesSection />

        {/* Comparison Section (Neo AI vs Others) */}
        <ComparisonSection />



        {/* PRICING */}
        <section className="pricing-section py-20 md:py-24 bg-white px-6 relative overflow-hidden" aria-label="Paket Harga NeoAI">
          <div className="absolute top-1/2 left-0 w-64 h-64 bg-[#CCD9E3] rounded-full blur-3xl opacity-50 -z-10" />

          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16 section-header">
              <h2
                className="text-3xl md:text-5xl font-bold mb-4 text-[#164361]"
                style={headingFont}
              >
                {t('pricing.title')}
              </h2>
              <p className="text-lg md:text-xl text-[#475569] max-w-2xl mx-auto">
                {t('pricing.subtitle')}
              </p>
            </div>
            <div className="flex overflow-x-auto overflow-y-hidden gap-4 pb-6 pt-8 px-4 md:px-0 snap-x items-stretch">
              {error && (
                <div className="md:col-span-3 text-center p-8 bg-red-50 text-red-700 rounded-lg border border-red-200">
                  ❌ <strong>Error:</strong> {t('pricing.error', { error })}. {t('pricing.checkConfig')}
                </div>
              )}

              {loading && !error ? (
                [1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-gray-100 rounded-3xl h-96 animate-pulse flex-shrink-0 w-[280px] snap-center"
                  />
                ))
              ) : (
                dbPlans.map((plan) => {
                  return (
                    <PricingCardWidget
                      key={plan.id}
                      plan={plan}
                      features={packageFeatures[plan.id] || []}
                      isPopular={plan.populer === true}
                      t={t}
                      onAuthAction={handleAuthAction}
                      checkingStatus={checkingStatus}
                    />
                  );

                })
              )}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        {/* FAQ Section (Moved after Pricing) */}
        <FAQ />

        {/* Final CTA Section */}
        <CallToAction onAuthAction={handleAuthAction} />


      </main>

      <Footer />
      <NetworkIconLoader visible={loading || !heroImagesLoaded} />
    </div >
  );
};

export default HomePage;
